import { useState, useEffect } from 'react'
import { get } from 'aws-amplify/api'
import { getCachedAuthSession } from 'services/authSessionCache'

import { ConsumerRTHData } from '@nexiahome/ui-common/themes/util/types'

type Props = {
  deviceId: string
}

type UseConsumerRthResult = {
  dailyData: ConsumerRTHData | undefined
  monthlyData: ConsumerRTHData | undefined
  isDailyDataLoaded: boolean
  isMonthlyDataLoaded: boolean
}

const useConsumerRth = ({ deviceId }: Props): UseConsumerRthResult => {
  const [dailyData, setDailyData] = useState<ConsumerRTHData>()
  const [monthlyData, setMonthlyData] = useState<ConsumerRTHData>()
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    (async () => {
      setIsLoaded(false)
      const fetchConsumerRth = async (granularity: string): Promise<ConsumerRTHData> => {
        const session = await getCachedAuthSession()
        const res = await get({
          apiName: 'dwaApi',
          path: `/consumer-rth/${deviceId}/${granularity}`,
          options: {
            headers: { 'Authorization': session?.tokens?.idToken?.toString() || '' }
          }
        }).response

        const data = await res.body.json()
        return data as unknown as ConsumerRTHData
      }

      try {
        const [dailyRes, monthlyRes] = await Promise.all([
          fetchConsumerRth('daily'),
          fetchConsumerRth('monthly')
        ])

        setDailyData(dailyRes)
        setMonthlyData(monthlyRes)
      } catch {
        setDailyData(undefined)
        setMonthlyData(undefined)
      } finally {
        setIsLoaded(true)
      }
    })()
  }, [deviceId])

  return {
    dailyData,
    monthlyData,
    isDailyDataLoaded: isLoaded,
    isMonthlyDataLoaded: isLoaded
  }
}

export default useConsumerRth
